<template>
	<div class="contacts-page">
		<page-title :name="pageName" :title="pageTitle" :breadcrumbs="pageBreadcrumbs" />
		<page-content :content="pageContent" />

		<div class="contact-us margin-bottom-80">
			<div class="container">
				<div class="contact-info">
					<div class="row">
						<div class="col-md-7 col-sm-12 col-xs-12">
							<div class="contact-form">
								<h4>{{ $t('Sentence.ContactsPageFormTitle') }}</h4>
								<span class="section-title-line-2 margin-bottom-40"></span>

								<ValidationObserver ref="form" v-slot="{ pristine }">
									<form autocomplete="off" @submit.prevent="sendForm">
										<form-errors-box :errors="errors"></form-errors-box>

										<div class="row margin-top-20">
											<div class="col-md-6 col-sm-12 col-xs-12">
												<form-input
													v-model="firstName"
													:show-errors="showErrors"
													label="Misc.FirstName"
													name="first_name"
													rules="required"
												></form-input>
											</div>
											<div class="col-md-6 col-sm-12 col-xs-12">
												<form-input
													v-model="lastName"
													:show-errors="showErrors"
													label="Misc.LastName"
													name="last_name"
												></form-input>
											</div>
											<div class="col-md-6 col-sm-12 col-xs-12">
												<form-input
													v-model="email"
													:show-errors="showErrors"
													label="Misc.Email"
													name="email"
													rules="required|email"
												></form-input>
											</div>
											<div class="col-md-6 col-sm-12 col-xs-12">
												<form-input
													v-model="phone"
													:show-errors="showErrors"
													label="Misc.Phone"
													name="phone"
													:rules="{ regex: /^([ +-.0-9]+)$/ }"
												></form-input>
											</div>
											<div class="col-md-12 col-sm-12 col-xs-12">
												<question-type-selector :selected="questionType"></question-type-selector>
											</div>
											<div class="col-md-12 col-sm-12 col-xs-12">
												<form-textarea
													v-model="message"
													:show-errors="showErrors"
													label="Misc.Message"
													name="message"
													rules="required"
												></form-textarea>
											</div>
											<div class="col-md-12 col-sm-12 col-xs-12">
												<div class="form-submit">
													<form-button
														:disabled="pristine || loading"
														:is-loading="loading"
														label="Button.Send"
														component-class="button-3"
													></form-button>
													<span>({{ $t('Sentence.AllFieldsMarkedWithStarAreMandatory') }})</span>
												</div>
											</div>
										</div>
									</form>
								</ValidationObserver>
							</div>
						</div>
						<div class="col-md-5 col-sm-12 col-xs-12">
							<div class="contact-details">
								<h4>&nbsp;</h4>
								<span class="section-title-line-2 transparent margin-bottom-40"></span>
								<ul>
									<li>
										<i class="fa fa-balance-scale"></i>
										<h4>{{ companyName }}</h4>
									</li>
									<li>
										<i class="fa fa-map-marker"></i>
										<h4>{{ companyAddressFull }}</h4>
									</li>
									<li>
										<i class="fa fa-info"></i>
										<h4>{{ $t('Misc.Vat') }} {{ companyVat }}</h4>
									</li>
									<!-- <li>
										<i class="fa fa-phone"></i>
										<h4>+ 000 123 456 7890</h4>
									</li> -->
									<!-- <li>
										<i class="fa fa-fax"></i>
										<h4>+ 000 123 456 0987</h4>
									</li> -->
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Config from '@/config'
import AccountInfo from '@/mixins/AccountInfo'
import FormButton from '@/components/atoms/FormButton'
import FormErrorsBox from '@/components/atoms/FormErrorsBox'
import FormInput from '@/components/atoms/FormInput'
import FormTextarea from '@/components/atoms/FormTextarea'
import PageContent from '@/components/partials/PageContent'
import PageInfo from '@/mixins/PageInfo'
import PageTitle from '@/components/partials/PageTitle'
import QuestionTypeSelector from '@/utils/commons/QuestionTypeSelector'

export default {
	name: 'Contacts',
	components: {
		FormButton,
		FormErrorsBox,
		FormInput,
		FormTextarea,
		PageContent,
		PageTitle,
		QuestionTypeSelector,
	},
	mixins: [AccountInfo, PageInfo],
	data() {
		return {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			questionType: '',
			message: '',
			loading: false,
			showErrors: false,
			errors: [],
		}
	},
	computed: {
		companyAddressFull() {
			return Config.COMPANY_LEGAL_ADDRESS_FULL
		},
		companyName() {
			return Config.COMPANY_NAME
		},
		companyVat() {
			return Config.COMPANY_VAT
		},
	},
	mounted() {
		// Listen for question type input updated event
		this.$root.$on('question-type-input-updated-event', (result) => {
			this.questionType = result.value
		})

		if (this.account) {
			// Prefill logged account data
			this.firstName = this.account.first_name
			this.lastName = this.account.last_name
			this.email = this.account.email
		}
	},
	destroyed() {
		// This prevents from having memory leaks
		this.$root.$off('question-type-input-updated-event')
	},
	methods: {
		sendForm() {
			this.$refs.form.validate().then((success) => {
				if (!success) {
					this.showErrors = true
					return false
				}

				this.loading = true

				// Perform questions sending
				axios
					.post('/questions', {
						first_name: this.firstName,
						last_name: this.lastName,
						email: this.email,
						phone: this.phone,
						type: this.questionType,
						message: this.message,
					})
					.then(() => {
						this.loading = false

						this.firstName = ''
						this.lastName = ''
						this.email = ''
						this.phone = ''
						this.questionType = ''
						this.message = ''

						// Reset form observer state
						requestAnimationFrame(() => {
							this.$refs.form.reset()
						})

						this.showToastSuccess('MessageAdded', 'OperationSuccess')
					})
					.catch((error) => {
						this.loading = false

						this.firstName = ''
						this.lastName = ''
						this.email = ''
						this.phone = ''
						this.questionType = ''
						this.message = ''

						// Reset form
						this.errors = error.response.data.info
							? Object.values(error.response.data.info)
							: new Array(error.response.data.message)

						// Reset form observer state
						requestAnimationFrame(() => {
							this.$refs.form.reset()
						})

						this.showToastError('ErrorAddingMessage', error)
					})
			})
		},
	},
}
</script>



// WEBPACK FOOTER //
// src/views/service/Contacts.vue